import { createRoot, type Root } from "react-dom/client";
import type { Channel } from "@/models/Channel";
import { registerClientMiddlewareNoHook } from "@/open_api/RegisterClientMiddleware";
import { agpIdToChannelMap } from "@/web_analytics/dataLayer/events/data/agpChannelMap";
import { initLazyGtm } from "./gtmLazyLoader";
import ReactWrapper from "./reactWrapper";
import { trackShadowEntryClick } from "./shadowEntryClickTracking";

type WebComponentManifestChunk = {
  src?: string;
  css?: string[];
  imports?: string[];
};

type WebComponentManifest = Record<string, WebComponentManifestChunk>;

export const defaultCssAssetPath = new URL(
  "webshop-fi-components.css",
  import.meta.url,
).toString();
const defaultManifestPath = new URL(
  "manifest.webcomponents.json",
  import.meta.url,
).toString();

let manifestLoadPromise: Promise<WebComponentManifest | null> | undefined;

const hasUrlScheme = (value: string) => {
  return /^[a-zA-Z][a-zA-Z\d+.-]*:/u.test(value);
};

export const resolveCssAssetPath = (cssPathAttribute: string | null) => {
  return cssPathAttribute ?? defaultCssAssetPath;
};

export const resolveStylesheetHref = (
  cssAssetPath: string,
  backendHost: string | null,
) => {
  if (backendHost && !hasUrlScheme(cssAssetPath)) {
    return `${backendHost}${cssAssetPath}`;
  }

  return cssAssetPath;
};

async function loadWebComponentManifest() {
  manifestLoadPromise ??= fetch(defaultManifestPath)
    .then(async (response) => {
      if (!response.ok) {
        return null;
      }

      return (await response.json()) as WebComponentManifest;
    })
    .catch(() => {
      return null;
    });

  return await manifestLoadPromise;
}

function findManifestChunkKey(
  manifest: WebComponentManifest,
  manifestEntrySource: string,
) {
  if (manifest[manifestEntrySource]) {
    return manifestEntrySource;
  }

  const found = Object.entries(manifest).find(([, chunk]) => {
    return chunk.src === manifestEntrySource;
  });

  return found?.[0];
}

function collectCssFromManifestChunk({
  manifest,
  chunkKey,
  visited,
  cssFiles,
}: {
  manifest: WebComponentManifest;
  chunkKey: string;
  visited: Set<string>;
  cssFiles: Set<string>;
}) {
  if (visited.has(chunkKey)) {
    return;
  }

  visited.add(chunkKey);
  const chunk = manifest[chunkKey];

  if (!chunk) {
    return;
  }

  chunk.css?.forEach((file) => {
    cssFiles.add(file);
  });

  for (const importedChunkKey of chunk.imports ?? []) {
    collectCssFromManifestChunk({
      manifest,
      chunkKey: importedChunkKey,
      visited,
      cssFiles,
    });
  }
}

class ProductShadowBase extends HTMLElement {
  protected shadow: ShadowRoot;
  protected backendHost: string | null | undefined;
  protected appPathPrefix: string | null | undefined;
  protected cssAssetPath: string = defaultCssAssetPath;
  protected manifestEntrySource?: string;
  protected skipCart: boolean = false;
  protected gtmContainerId: string | null | undefined;
  // Factory for producing children; stored to allow re-rendering on attribute changes
  private childrenFactory?: () => React.ReactNode;
  // Cache a single React root to avoid duplicate mounts
  private reactRoot?: Root;
  private hasShadowClickTracking = false;
  private resourceLoadPromise?: Promise<void>;

  private readonly handleShadowClick = (event: Event) => {
    trackShadowEntryClick(event);
  };

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
    this.ensureShadowClickTracking();
  }

  // Runtime config is static after mount.
  static get observedAttributes() {
    return [];
  }

  // Public property accessors for channel (maps to the 'channel' attribute)
  get channel(): Channel | null {
    return (this.getAttribute("channel") as Channel) ?? null;
  }

  set channel(value: string | null) {
    if (value === null || value === undefined) {
      this.removeAttribute("channel");
    } else {
      this.setAttribute("channel", value);
    }
  }

  get language(): string | null {
    return this.getAttribute("language") ?? null;
  }

  set language(value: string | null) {
    if (value === null || value === undefined) {
      this.removeAttribute("language");
    } else {
      this.setAttribute("language", value);
    }
  }

  attributeChangedCallback(
    _name: string,
    _oldValue: string | null,
    _newValue: string | null,
  ) {
    // Runtime config is treated as static after mount.
  }

  loadAndRender(
    getChildren: () => React.ReactNode,
    prefetchData?: () => void | Promise<void>,
  ) {
    this.ensureShadowClickTracking();
    // Store for potential re-renders on attribute changes
    this.childrenFactory = getChildren;
    this.backendHost = this.getAttribute("backend-host");
    this.appPathPrefix = this.getAttribute("app-path-prefix");
    globalThis.webComponentAppPathPrefix = this.appPathPrefix ?? undefined;
    this.cssAssetPath = resolveCssAssetPath(this.getAttribute("css-path"));
    globalThis.recaptchaSiteKey =
      this.getAttribute("recaptcha-site-key") ?? undefined;
    this.gtmContainerId = this.getAttribute("gtm-container-id");
    this.skipCart = this.getAttribute("skip-cart") === "true";

    registerClientMiddlewareNoHook(this.backendHost ?? undefined, true);
    // Derive seller-id from agpId query parameter if not already explicitly set.
    try {
      const url = new URL(globalThis.location.href);
      const agpIdParam = url.searchParams.get("agpId");
      if (agpIdParam && !this.getAttribute("channel")) {
        const parsed = Number(agpIdParam);
        if (!Number.isNaN(parsed) && parsed in agpIdToChannelMap) {
          const mappedSeller = agpIdToChannelMap[parsed];
          if (mappedSeller) {
            this.setAttribute("channel", mappedSeller);
          }
        }
      }
    } catch {
      // Silently ignore URL parsing issues (unlikely in browser environment)
    }

    if (this.gtmContainerId) {
      initLazyGtm(this.gtmContainerId, 6000);
    }
    const prefetchPromise = prefetchData?.();
    if (prefetchPromise) {
      prefetchPromise.catch((err) => {
        console.error(err);
      });
    }

    const resourceLoadPromise =
      this.resourceLoadPromise ?? this.loadResources(this.backendHost);

    resourceLoadPromise
      .then(() => {
        this.resourceLoadPromise = undefined;
        return this.renderComponent();
      })
      .catch((err) => {
        this.resourceLoadPromise = undefined;
        console.error(err);
      });
  }

  protected primeResourceLoading() {
    this.backendHost = this.getAttribute("backend-host");
    this.cssAssetPath = resolveCssAssetPath(this.getAttribute("css-path"));
    this.resourceLoadPromise ??= this.loadResources(this.backendHost);
  }

  async loadResources(backendHost: string | null) {
    const hrefs = await this.resolveStylesheetHrefs(backendHost);

    if (this.supportsConstructableStylesheets()) {
      await this.appendConstructableStylesheets(hrefs);
      return;
    }

    await Promise.all(hrefs.map((href) => this.appendStylesheetLink(href)));
  }

  private async resolveStylesheetHrefs(backendHost: string | null) {
    const customCssPath = this.getAttribute("css-path");
    if (customCssPath) {
      return [resolveStylesheetHref(customCssPath, backendHost)];
    }

    if (this.manifestEntrySource) {
      const manifest = await loadWebComponentManifest();
      if (manifest) {
        const entryChunkKey = findManifestChunkKey(
          manifest,
          this.manifestEntrySource,
        );
        if (entryChunkKey) {
          const cssFiles = new Set<string>();
          const visited = new Set<string>();
          collectCssFromManifestChunk({
            manifest,
            chunkKey: entryChunkKey,
            visited,
            cssFiles,
          });

          const indexChunkKey = findManifestChunkKey(
            manifest,
            "src/web_components/index.ts",
          );
          if (indexChunkKey) {
            collectCssFromManifestChunk({
              manifest,
              chunkKey: indexChunkKey,
              visited,
              cssFiles,
            });
          }

          if (cssFiles.size > 0) {
            return Array.from(cssFiles).map((file) =>
              resolveStylesheetHref(
                new URL(file, import.meta.url).toString(),
                backendHost,
              ),
            );
          }
        }
      }
    }

    return [resolveStylesheetHref(this.cssAssetPath, backendHost)];
  }

  private appendStylesheetLink(href: string) {
    return new Promise<void>((resolve, reject) => {
      const linkElement = document.createElement("link");
      linkElement.setAttribute("rel", "stylesheet");
      linkElement.setAttribute("href", href);

      linkElement.onload = () => {
        resolve();
      };

      linkElement.onerror = () => {
        reject(new Error(`Failed to load stylesheet: ${href}`));
      };

      this.shadow.appendChild(linkElement);
    });
  }

  private supportsConstructableStylesheets() {
    return (
      typeof CSSStyleSheet !== "undefined" &&
      "replaceSync" in CSSStyleSheet.prototype &&
      "adoptedStyleSheets" in this.shadow
    );
  }

  private async appendConstructableStylesheets(hrefs: string[]) {
    const stylesheets = await Promise.all(
      hrefs.map(async (href) => {
        const response = await fetch(href);
        if (!response.ok) {
          throw new Error(`Failed to load stylesheet: ${href}`);
        }

        const cssText = await response.text();
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(cssText);
        return sheet;
      }),
    );

    this.shadow.adoptedStyleSheets = [
      ...this.shadow.adoptedStyleSheets,
      ...stylesheets,
    ];
  }

  renderComponent() {
    if (!this.isConnected) {
      return;
    }

    this.reactRoot ??= createRoot(this.shadow);
    const node = this.childrenFactory?.();
    if (node) {
      this.reactRoot.render(
        <ReactWrapper
          backendHost={this.backendHost ?? undefined}
          appPathPrefix={this.appPathPrefix ?? undefined}
          skipCart={this.skipCart}
          channel={this.channel ?? undefined}
          language={this.language ?? undefined}
          enableInputAutoComplete={
            this.getAttribute("enable-input-auto-complete") === "true"
          }
        >
          {node}
        </ReactWrapper>,
      );
    }
  }

  disconnectedCallback() {
    if (this.hasShadowClickTracking) {
      this.shadow.removeEventListener("click", this.handleShadowClick);
      this.hasShadowClickTracking = false;
    }

    this.reactRoot?.unmount();
    this.reactRoot = undefined;
    this.childrenFactory = undefined;
  }

  private ensureShadowClickTracking() {
    if (this.hasShadowClickTracking) {
      return;
    }

    this.shadow.addEventListener("click", this.handleShadowClick);
    this.hasShadowClickTracking = true;
  }
}

export default ProductShadowBase;
